import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { AuthTokens, AuthUser } from '@/types/auth';

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  // The IsoSofts `auth_token` this ESD session was minted from (via SSO). Used to
  // detect when the suite identity changed (logout / different account on another
  // subdomain) so the persisted ESD session can be invalidated. Optional on
  // setSession: omitting it (e.g. silent token refresh) preserves the existing value.
  isosoftsToken: string | null;
  setSession: (tokens: AuthTokens, user: AuthUser, isosoftsToken?: string) => void;
  setAccessToken: (accessToken: string) => void;
  setUser: (user: AuthUser | null) => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isosoftsToken: null,
      setSession: (tokens, user, isosoftsToken) =>
        set((state) => ({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          user,
          isosoftsToken: isosoftsToken !== undefined ? isosoftsToken : state.isosoftsToken,
        })),
      setAccessToken: (accessToken) => set({ accessToken }),
      setUser: (user) => set({ user }),
      clear: () => set({ accessToken: null, refreshToken: null, user: null, isosoftsToken: null }),
    }),
    {
      name: 'esd.auth',
      // refreshToken + user + the source IsoSofts token survive reload so the
      // session stays bound to the suite identity. Access token is refreshed on demand.
      partialize: (state) => ({
        refreshToken: state.refreshToken,
        user: state.user,
        isosoftsToken: state.isosoftsToken,
      }),
    },
  ),
);
